import { useState, useEffect } from 'react'
import {
    Box,
    Card,
    CardContent,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Chip,
    IconButton,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Grid,
    Divider,
    LinearProgress
} from '@mui/material'
import {
    Visibility as ViewIcon,

} from '@mui/icons-material'
import { collection, query, orderBy, onSnapshot, doc, updateDoc, Timestamp } from 'firebase/firestore'
import { getFirebaseDb } from '../../config/firebase'
import { formatCurrency, formatDateTime, getOrderStatusColor } from '../../utils'
import type { Order, OrderStatus } from '../../types'
import toast from 'react-hot-toast'

const statusOptions: OrderStatus[] = ['pending', 'confirmed', 'processing', 'shipped', 'delivered', 'cancelled']

export default function OrdersPage() {
    const [orders, setOrders] = useState<Order[]>([])
    const [loading, setLoading] = useState(true)
    const [statusFilter, setStatusFilter] = useState<string>('all')
    const [selectedOrder, setSelectedOrder] = useState<Order | null>(null)
    const [dialogOpen, setDialogOpen] = useState(false)

    const db = getFirebaseDb()

    useEffect(() => {
        const ordersQuery = query(
            collection(db, 'orders'),
            orderBy('createdAt', 'desc')
        )

        const unsubscribe = onSnapshot(ordersQuery, (snapshot) => {
            const orderData = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            })) as Order[]
            setOrders(orderData)
            setLoading(false)
        })

        return () => unsubscribe()
    }, [db])

    const filteredOrders = statusFilter === 'all'
        ? orders
        : orders.filter(o => o.status === statusFilter)

    const handleStatusChange = async (orderId: string, newStatus: OrderStatus) => {
        try {
            await updateDoc(doc(db, 'orders', orderId), {
                status: newStatus,
                updatedAt: Timestamp.now()
            })
            toast.success(`Order status updated to ${newStatus}`)
        } catch (error) {
            toast.error('Failed to update order status')
        }
    }

    const handleViewOrder = (order: Order) => {
        setSelectedOrder(order)
        setDialogOpen(true)
    }

    return (
        <Box>
            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h5" sx={{ fontWeight: 600 }}>
                    Orders ({filteredOrders.length})
                </Typography>
                <FormControl size="small" sx={{ minWidth: 150 }}>
                    <InputLabel>Status</InputLabel>
                    <Select
                        value={statusFilter}
                        label="Status"
                        onChange={(e) => setStatusFilter(e.target.value)}
                    >
                        <MenuItem value="all">All Orders</MenuItem>
                        {statusOptions.map(status => (
                            <MenuItem key={status} value={status} sx={{ textTransform: 'capitalize' }}>
                                {status}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Box>

            {/* Orders Table */}
            <Card>
                <CardContent>
                    {loading ? (
                        <LinearProgress />
                    ) : filteredOrders.length === 0 ? (
                        <Typography variant="body2" sx={{ color: 'grey.500', textAlign: 'center', py: 4 }}>
                            No orders found
                        </Typography>
                    ) : (
                        <TableContainer component={Paper} sx={{ backgroundColor: 'transparent' }}>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Order ID</TableCell>
                                        <TableCell>Customer</TableCell>
                                        <TableCell>Items</TableCell>
                                        <TableCell>Total</TableCell>
                                        <TableCell>Status</TableCell>
                                        <TableCell>Date</TableCell>
                                        <TableCell align="right">Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {filteredOrders.map((order) => (
                                        <TableRow key={order.id} hover>
                                            <TableCell>
                                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                    #{order.invoiceNumber || order.id.slice(0, 8)}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2">
                                                    {order.guestInfo?.name || 'Guest'}
                                                </Typography>
                                                <Typography variant="caption" sx={{ color: 'grey.500' }}>
                                                    {order.guestInfo?.phone}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2">
                                                    {order.items?.length || 0} items
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                    {formatCurrency(order.pricing?.total || 0)}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Select
                                                    size="small"
                                                    value={order.status}
                                                    onChange={(e) => handleStatusChange(order.id, e.target.value as OrderStatus)}
                                                    sx={{ minWidth: 120 }}
                                                >
                                                    {statusOptions.map(status => (
                                                        <MenuItem key={status} value={status}>
                                                            <Chip
                                                                label={status}
                                                                color={getOrderStatusColor(status)}
                                                                size="small"
                                                                sx={{ textTransform: 'capitalize' }}
                                                            />
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2">
                                                    {formatDateTime(order.createdAt)}
                                                </Typography>
                                            </TableCell>
                                            <TableCell align="right">
                                                <IconButton size="small" onClick={() => handleViewOrder(order)}>
                                                    <ViewIcon fontSize="small" />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </CardContent>
            </Card>

            {/* Order Detail Dialog */}
            <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
                <DialogTitle>
                    Order Details - #{selectedOrder?.invoiceNumber || selectedOrder?.id.slice(0, 8)}
                </DialogTitle>
                <DialogContent>
                    {selectedOrder && (
                        <Grid container spacing={3} sx={{ mt: 1 }}>
                            <Grid item xs={12} md={6}>
                                <Typography variant="subtitle2" sx={{ color: 'grey.500', mb: 1 }}>
                                    Customer Information
                                </Typography>
                                <Typography variant="body2">
                                    <strong>Name:</strong> {selectedOrder.guestInfo?.name || 'N/A'}
                                </Typography>
                                <Typography variant="body2">
                                    <strong>Email:</strong> {selectedOrder.guestInfo?.email || 'N/A'}
                                </Typography>
                                <Typography variant="body2">
                                    <strong>Phone:</strong> {selectedOrder.guestInfo?.phone || 'N/A'}
                                </Typography>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Typography variant="subtitle2" sx={{ color: 'grey.500', mb: 1 }}>
                                    Delivery Address
                                </Typography>
                                <Typography variant="body2">
                                    {selectedOrder.deliveryAddress?.street || 'N/A'}
                                </Typography>
                                <Typography variant="body2">
                                    {selectedOrder.deliveryAddress?.city}, {selectedOrder.deliveryAddress?.province}
                                </Typography>
                            </Grid>
                            <Grid item xs={12}>
                                <Divider sx={{ my: 2 }} />
                                <Typography variant="subtitle2" sx={{ color: 'grey.500', mb: 2 }}>
                                    Order Items
                                </Typography>
                                {selectedOrder.items?.map((item, index) => (
                                    <Box key={index} sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                        <Typography variant="body2">
                                            {item.productName} x {item.quantity}
                                        </Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                            {formatCurrency(item.totalPrice)}
                                        </Typography>
                                    </Box>
                                ))}
                                <Divider sx={{ my: 2 }} />
                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                        Total
                                    </Typography>
                                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                        {formatCurrency(selectedOrder.pricing?.total || 0)}
                                    </Typography>
                                </Box>
                            </Grid>
                        </Grid>
                    )}
                </DialogContent>
                <DialogActions sx={{ p: 3 }}>
                    <Button onClick={() => setDialogOpen(false)}>Close</Button>
                </DialogActions>
            </Dialog>
        </Box>
    )
}
